import React, { useState, useEffect, useRef } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  IconButton,
  Typography,
  Button,
  Box,
} from '@mui/material';
import { ArrowLeftCircle, Edit, PlusCircle, Trash } from 'react-feather';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import AddLeg from './AddLeg';
import EditLeg from './EditLeg';
import CreateRouteForm from './CreateRouteForm';
import api from '../network/ApiClient';

const EditRoutes = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [legs, setLegs] = useState([]);
  const [locations, setLocations] = useState({});
  const [editLeg, setEditLeg] = useState(null);
  const [showNewLegInputs, setShowNewLegInputs] = useState(false);
  const [routename, setRouteName] = useState('');
  const [routeDetail, setRouteDetail] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const bottomRef = useRef(null);

  const fetchRoute = async () => {
    try {
      const res = await api.get(`${process.env.REACT_APP_API_URL}route/${id}`);
      if (res.data && res.data[0]) {
        // Sort legs based on the 'sequence' field
        const sortedLegs = res.data[0].legs.sort((a, b) => a.sequence - b.sequence);
        setLegs(sortedLegs || []);
        setRouteName(res.data[0].name);
        // Full detail (name, organizergroup, cancellation) for the edit modal.
        setRouteDetail(res.data[0]);
      } else {
        navigate('/routes');
      }
    } catch (error) {
      navigate('/routes');
    }
  };

  const parseDuration = (durationStr) => {
    const [hours, minutes, seconds] = durationStr.split(':').map(Number);
    return {
      hours: String(hours).padStart(2, '0'),
      minutes: String(minutes).padStart(2, '0'),
      seconds: String(seconds).padStart(2, '0'),
    };
  };

  useEffect(() => {
    const getAllLocations = async () => {
      try {
        const res = await api.get(`${process.env.REACT_APP_API_URL}location`);
        const locationMap = res.data.reduce((map, location) => {
          map[location.id] = location.name;
          return map;
        }, {});
        setLocations(locationMap);
      } catch (error) {
        console.log(error);
      }
    };

    fetchRoute();
    getAllLocations();
  }, [id, navigate]);

  const handleDelete = async (ff) => {
    try {
      const res = await api.post(`${process.env.REACT_APP_API_URL}route/${id}/removeleg/${ff}`);
      // The leg endpoint can return a body carrying `error` (see ROUTES_API.md in
      // the KB) — don't drop the leg from the table unless it was really removed.
      if (res.data?.error || !res.data?.removed) {
        toast.error(res.data?.error || 'Could not delete leg. Please try again.');
        return;
      }
      // Re-read so the remaining legs keep their correct sequence numbering.
      await fetchRoute();
      toast.success('Leg deleted');
    } catch (error) {
      console.error('Error deleting leg:', error);
      toast.error(error?.response?.data?.error || 'Could not delete leg. Please try again.');
    }
  };

  return (
    <div style={{ padding: '20px' }}>
      <Typography variant="h6" gutterBottom>
        <IconButton sx={{ color: 'black' }} onClick={() => navigate('/routes')}>
          <ArrowLeftCircle />
        </IconButton>
        {routename}
        <IconButton sx={{ color: 'black' }} onClick={() => setEditModalOpen(true)} title="Edit route details">
          <Edit />
        </IconButton>
      </Typography>

      {routeDetail ? (
        <CreateRouteForm
          edit={routeDetail}
          modalShow={editModalOpen}
          setModalShow={setEditModalOpen}
          onSubmitSuccess={fetchRoute}
        />
      ) : null}
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '10%' }}>Leg</TableCell>
              <TableCell sx={{ width: '20%' }}>Stop</TableCell>
              <TableCell sx={{ width: '15%' }}>Duration</TableCell>
              <TableCell sx={{ width: '15%' }}>Dwell</TableCell>
              <TableCell sx={{ width: '10%' }}>Distance (km)</TableCell>
              <TableCell sx={{ width: '10%' }}>Cost (N$)</TableCell>
              <TableCell sx={{ width: '10%' }}>Embark</TableCell>
              <TableCell sx={{ width: '10%' }}>Disembark</TableCell>
              <TableCell sx={{ width: '10%' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {legs.map((leg) => (
              <TableRow key={leg.id}>
                <TableCell>{leg.sequence === 1 ? 'Start' : `Stop ${leg.sequence}`}</TableCell>
                <TableCell sx={{ width: '200px' }}>{locations[leg.location] || 'Unknown Location'}</TableCell>
                <TableCell>{leg.eta}</TableCell>
                <TableCell>{leg.dwell}</TableCell>
                <TableCell>{leg.distance}</TableCell>
                <TableCell>{leg.sequence === 1 ? '' : leg.cost ? `N$ ${leg.cost}` : ''}</TableCell>
                <TableCell>
                  {leg.sequence !== legs.length && (
                    <Checkbox checked={leg.access === '2' || leg.access === '3' || leg.sequence === 1} disabled />
                  )}
                </TableCell>
                <TableCell>
                  {leg.sequence !== 1 && (
                    <Checkbox
                      checked={leg.access === '1' || leg.access === '3' || leg.sequence === legs.length}
                      disabled
                    />
                  )}
                </TableCell>
                <TableCell>
                  {leg.sequence !== 1 && (
                    <>
                      <IconButton
                        sx={{ color: 'red' }}
                        size="small"
                        onClick={() => {
                          const consent = window.confirm('Do you want to delete this Leg?');
                          if (!consent) return;
                          handleDelete(leg.id);
                        }}
                      >
                        <Trash size={20} />
                      </IconButton>
                      <IconButton
                        sx={{ color: 'blue' }}
                        size="small"
                        onClick={() => {
                          setShowNewLegInputs(false);
                          setEditLeg({
                            ...leg,
                            embark: leg.access === '3' || leg.access === '2',
                            disembark: leg.access === '3' || leg.access === '1',
                            eta: parseDuration(leg.eta),
                            dwell: parseDuration(leg.dwell),
                          });
                          bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
                        }}
                      >
                        <Edit size={20} />
                      </IconButton>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box ref={bottomRef} sx={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
        {!showNewLegInputs && editLeg === null && (
          <Button
            variant="outlined"
            color="primary"
            startIcon={<PlusCircle />}
            onClick={() => {
              setEditLeg(null);
              setShowNewLegInputs(true);
            }}
            sx={{ width: '200px', height: '45px' }}
          >
            Add New Leg
          </Button>
        )}
      </Box>
      {showNewLegInputs && editLeg === null && (
        <AddLeg locations={locations} setShowNewLegInputs={setShowNewLegInputs} id={id} fetchRoute={fetchRoute} />
      )}
      {editLeg && showNewLegInputs === false && (
        <EditLeg
          locations={locations}
          leg={editLeg}
          setLegs={setLegs}
          setEditLeg={setEditLeg}
          id={id}
          fetchRoute={fetchRoute}
        />
      )}
    </div>
  );
};

export default EditRoutes;
